import {PrimeIcons} from "primereact/api";
import {Card} from "primereact/card";

import {buildStreetWithHouseNumber, type GeoRefAddress} from "@reva/shared/geo-reference";

import {useRevaTranslation} from "../../hooks/languageHooks";
import {buildAddressQuery} from "../../util/externalMapLinks";
import {formatLatLon} from "../../util/numberFormat";
import MapLinks from "./MapLinks";

interface AddressCardProps {
    /** selected address meta */
    addressMeta: GeoRefAddress | null | undefined;
    /** optional additional CSS classes */
    className?: string;
    /** flag, if the address should be marked as reference address */
    isReferenceAddress?: boolean;
    /** optional comment to show, if the address is rendered as a reference address */
    referenceAddressComment?: string | null;
    /** event handler for clicking on the address card */
    onSelectAddress?: (addressMeta: GeoRefAddress) => void;
    /** event handler for hovering over the address card */
    onHoverAddress?: (addressMeta: GeoRefAddress) => void;
}

/**
 * Display Card with address meta.
 */
export default function AddressCard({
    addressMeta,
    className,
    isReferenceAddress,
    referenceAddressComment,
    onSelectAddress,
    onHoverAddress,
}: AddressCardProps) {
    const {t, language} = useRevaTranslation();

    if (!addressMeta) return null;

    const onAddressClick = () => onSelectAddress?.(addressMeta);

    const onAddressMouseEnter = () => onHoverAddress?.(addressMeta);

    return (
        <Card
            className={
                "AddressCard bg-surface-100 dark:bg-surface-700 hover:bg-surface-300 dark:hover:bg-surface-500 " +
                (className ? ` ${className}` : "")
            }
            onClick={onAddressClick}
            onMouseEnter={onAddressMouseEnter}
        >
            <div className="font-bold text-lg mb-2 pb-2 border-b flex items-center gap-2">
                <span className="flex-1">
                    <span className={`${PrimeIcons.MAP_MARKER} mr-2`} />
                    {language === "en" ? addressMeta.displayNameEN : addressMeta.displayNameDE}
                </span>
                <MapLinks mode="address" query={buildAddressQuery(addressMeta)} className="text-base font-normal" />
            </div>
            <div className="AddressRow mb-4">
                <div className="AddressIcon" />
                <div className="AddressValue flex items-center">
                    <span className="flex-1">
                        Lat: {formatLatLon(addressMeta.lat, language)} Lng: {formatLatLon(addressMeta.lon, language)}
                    </span>
                    <MapLinks mode="coordinate" lat={addressMeta.lat} lon={addressMeta.lon} />
                </div>
            </div>
            <div className="AddressRow mb-1">
                <div className="AddressIcon" />
                <div className="AddressLabel">{t("address.street")}:</div>
                <div className="AddressValue">
                    {buildStreetWithHouseNumber(addressMeta.street, addressMeta.houseNumber)}
                </div>
            </div>
            <div className="AddressRow mb-1">
                <div className="AddressIcon">
                    <span className={PrimeIcons.MAP} />
                </div>
                <div className="AddressLabel">{t("address.postcode")}:</div>
                <div className="AddressValue">{addressMeta.postcode}</div>
            </div>
            <div className="AddressRow mb-1">
                <div className="AddressIcon">
                    <span className={PrimeIcons.MAP} />
                </div>
                <div className="AddressLabel">{t("address.cadastral_district")}:</div>
                <div className="AddressValue">{addressMeta.cadastral_district}</div>
            </div>
            {addressMeta.cadastral_parcel_number && (
                <>
                    <div className="AddressRow mb-1">
                        <div className="AddressIcon">
                            <span className={PrimeIcons.MAP} />
                        </div>
                        <div className="AddressLabel">{t("address.cadastral_district_key")}:</div>
                        <div className="AddressValue">{addressMeta.cadastral_district_key}</div>
                    </div>
                    <div className="AddressRow mb-1">
                        <div className="AddressIcon" />
                        <div className="AddressLabel">{t("address.cadastral_key")}:</div>
                        <div className="AddressValue">{addressMeta.cadastral_key}</div>
                    </div>
                    <div className="AddressRow mb-1">
                        <div className="AddressIcon" />
                        <div className="AddressLabel">{t("address.cadastral_parcel_number")}:</div>
                        <div className="AddressValue">{addressMeta.cadastral_parcel_number}</div>
                    </div>
                </>
            )}
            <div className="AddressRow mb-1">
                <div className="AddressIcon">
                    <span className={PrimeIcons.MAP} />
                </div>
                <div className="AddressLabel">{t("address.municipality")}:</div>
                <div className="AddressValue">{addressMeta.municipality}</div>
            </div>
            <div className="AddressRow mb-1">
                <div className="AddressIcon">
                    <span className={PrimeIcons.MAP} />
                </div>
                <div className="AddressLabel">{t("address.district")}:</div>
                <div className="AddressValue">{addressMeta.district}</div>
            </div>
            <div className="AddressRow mb-1">
                <div className="AddressIcon">
                    <span className={PrimeIcons.MAP} />
                </div>
                <div className="AddressLabel">{t("address.region")}:</div>
                <div className="AddressValue">{addressMeta.region}</div>
            </div>
            <div className="AddressRow">
                <div className="AddressIcon">
                    <span className={PrimeIcons.MAP} />
                </div>
                <div className="AddressLabel">{t("address.state")}:</div>
                <div className="AddressValue">{addressMeta.state}</div>
            </div>
            {isReferenceAddress && (
                <div className="italic text-sm mt-2 pl-6 pt-2 border-t">
                    {t("address.referenceAddress")}
                    {referenceAddressComment && (
                        <div className="mt-1">
                            {t("address.referenceAddressComment", {comment: referenceAddressComment})}
                        </div>
                    )}
                </div>
            )}
        </Card>
    );
}
